import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useEffect, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Admin · Buddha KA" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AdminLayout,
});

type Status = "loading" | "denied" | "ok";

const NAV = [
  { to: "/admin", label: "Overview", exact: true },
  { to: "/admin/launch", label: "Launch" },
  { to: "/admin/analytics", label: "Analytics" },
  { to: "/admin/journey", label: "Journey" },
  { to: "/admin/creator", label: "Creator" },
  { to: "/admin/contact", label: "Contact" },
  { to: "/admin/releases", label: "Releases" },
  { to: "/admin/activations", label: "Activations" },
  { to: "/admin/content", label: "Content Ops" },
  { to: "/admin/content-engine", label: "Content Engine" },
  { to: "/admin/moderation", label: "Moderation" },
  { to: "/admin/pipeline", label: "Pipeline" },
  { to: "/admin/wisdom", label: "Wisdom" },
  { to: "/admin/books", label: "Books" },
  { to: "/admin/library", label: "Library" },
  { to: "/admin/academy", label: "Academy" },
  { to: "/admin/shop", label: "Shop" },
  { to: "/admin/bkls", label: "BKLS" },
  { to: "/admin/commerce", label: "Commerce" },
  { to: "/admin/events", label: "Events" },
  { to: "/admin/subscriptions", label: "Members" },
  { to: "/admin/customers", label: "Customers" },
  { to: "/admin/feedback", label: "Feedback" },
  { to: "/admin/authority", label: "Authority" },
  { to: "/admin/security", label: "Security" },

  { to: "/admin/auth-email", label: "Auth & Email" },
  { to: "/admin/ops", label: "Ops" },
];

function AdminLayout() {
  const [status, setStatus] = useState<Status>("loading");
  const [email, setEmail] = useState("");
  const location = useLocation();

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!alive) return;
      const user = userData.user;
      if (!user) {
        setStatus("denied");
        return;
      }
      setEmail(user.email ?? "");
      const { data: isAdmin, error } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: "admin",
      });
      if (!alive) return;
      setStatus(error || !isAdmin ? "denied" : "ok");
    })();
    return () => {
      alive = false;
    };
  }, []);

  async function signOut() {
    await supabase.auth.signOut();
    window.location.href = "/";
  }

  if (status === "loading") {
    return (
      <div className="mx-auto max-w-6xl px-4 py-24 sm:px-6">
        <p className="text-sm text-muted-foreground">Verifying access…</p>
      </div>
    );
  }

  if (status === "denied") {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 sm:px-6 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Restricted</p>
        <h1 className="mt-4 font-serif text-4xl">This gate is admin-only.</h1>
        <p className="mt-4 text-sm text-muted-foreground">
          Your account does not carry the admin role.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Link to="/" className="rounded-full border hairline px-5 py-2.5 text-sm">Return home</Link>
          <button onClick={signOut} className="rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian">
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6">
      <header className="flex flex-wrap items-baseline justify-between gap-4 border-b hairline pb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Admin</p>
          <h1 className="mt-2 font-serif text-4xl">The steward's room.</h1>
          <p className="mt-1 text-xs text-muted-foreground">{email}</p>
        </div>
        <button onClick={signOut} className="text-sm underline underline-offset-4 text-muted-foreground hover:text-foreground">
          Sign out
        </button>
      </header>

      <nav aria-label="Admin sections" className="mt-6 flex flex-wrap gap-2 border-b hairline pb-4">
        {NAV.map((item) => {
          const active = item.exact
            ? location.pathname === item.to
            : location.pathname.startsWith(item.to);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`rounded-full px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition ${
                active
                  ? "bg-gold-gradient text-obsidian font-semibold"
                  : "border hairline text-muted-foreground hover:text-foreground"
              }`}
            >
              {item.label}
            </Link>
          );
        })}
      </nav>

      <div className="mt-10">
        <Outlet />
      </div>
    </div>
  );
}

export function AdminSection({ title, description, action, children }: {
  title: string;
  description?: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-2xl text-gold-luminous">{title}</h2>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
        {action}
      </div>
      <div className="mt-6">{children}</div>
    </section>
  );
}
